import { HttpTransportType, HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import reduxSignalRMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-signalr-middleware";
import { RootState } from "_shared/_stores/root-reducer";
import { getSignalRReconnectDelaysMs } from "_shared/_stores/realtime/signalr-reconnect-policy";
import { AppDispatch } from "_shared/_stores/store-types";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import * as Constants from "config";
import Session from "users/__session/session";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import { proposalsApi } from "work/proposals/_api/proposal-api";
import ProposalSchema, { type ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import {
  addProposalJSON,
  recordLatestRevisionUpdate,
  removeProposal,
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
} from "work/proposals/_store/proposals-redux-slice";
import Proposal from "work/proposals/proposal";

export const proposalHubConnection = new HubConnectionBuilder()
  .configureLogging(LogLevel.Debug)
  .withUrl(`${Constants.apiBaseUrl}/proposalHub`, {
    skipNegotiation: true,
    transport: HttpTransportType.WebSockets,
    accessTokenFactory: () => Session.loadFromStorage(() => {}).authToken?.value ?? "",
  })
  .withAutomaticReconnect(getSignalRReconnectDelaysMs())
  .build();

interface ProposalDeletedEvent {
  id: string;
}

const getPartialProposalUpdate = (
  payload: unknown,
): Partial<
  Pick<ProposalJSON, "lastUpdated" | "latestRevisionId" | "supersededById" | "partyRevision" | "userRevision">
> | undefined => {
  const payloadRecord = payload as Record<string, unknown>;
  const partialProposalKeys = [
    "latestRevisionId",
    "supersededById",
    "partyRevision",
    "userRevision",
  ] as const satisfies readonly (keyof ProposalJSON)[];
  const hasPartialProposalUpdate = partialProposalKeys.some((key) => key in payloadRecord);
  if (!hasPartialProposalUpdate) return undefined;

  const update: Partial<
    Pick<ProposalJSON, "lastUpdated" | "latestRevisionId" | "supersededById" | "partyRevision" | "userRevision">
  > = {};
  if ("lastUpdated" in payloadRecord) update.lastUpdated = payloadRecord.lastUpdated as ProposalJSON["lastUpdated"];
  if ("latestRevisionId" in payloadRecord) {
    update.latestRevisionId = payloadRecord.latestRevisionId as ProposalJSON["latestRevisionId"];
  }
  if ("supersededById" in payloadRecord) {
    update.supersededById = payloadRecord.supersededById as ProposalJSON["supersededById"];
  }
  if ("partyRevision" in payloadRecord) {
    update.partyRevision = payloadRecord.partyRevision as ProposalJSON["partyRevision"];
  }
  if ("userRevision" in payloadRecord) {
    update.userRevision = payloadRecord.userRevision as ProposalJSON["userRevision"];
  }

  return update;
};

const getSupersededProposalIdValue = (proposalJSON: ProposalJSON): string | undefined =>
  proposalJSON.supersedes?.id ?? proposalJSON.originalProposal?.id ?? undefined;

const getPreviousLatestRevisionIdValue = (proposalJSON: ProposalJSON): string | undefined =>
  proposalJSON.supersedes?.latestRevisionId ?? proposalJSON.originalProposal?.latestRevisionId ?? undefined;

const recordLatestRevisionUpdateFromProposal = ({
  dispatch,
  incomingProposalJSON,
}: {
  dispatch: AppDispatch;
  incomingProposalJSON: ProposalJSON;
}) => {
  if (!incomingProposalJSON.id) return;

  const supersededProposalIdValue = getSupersededProposalIdValue(incomingProposalJSON);
  const previousLatestRevisionId = getPreviousLatestRevisionIdValue(incomingProposalJSON) ?? supersededProposalIdValue;
  if (!previousLatestRevisionId) return;

  dispatch(
    recordLatestRevisionUpdate({
      previousLatestRevisionId,
      latestRevisionId: incomingProposalJSON.id,
      supersededProposalId: supersededProposalIdValue,
    }),
  );
};

const invalidateProposalIndexes = (dispatch: AppDispatch) => {
  dispatch(
    proposalsApi.util.invalidateTags([
      { type: "Proposal", id: "LIST" },
      { type: "Proposal", id: "DraftCount" },
      { type: "Proposal", id: "ActiveCount" },
      { type: "Proposal", id: "ArchiveCount" },
    ]),
  );
};

const upsertProposalById = ({
  dispatch,
  proposalJSON,
  proposalIdValue,
  session,
}: {
  dispatch: AppDispatch;
  proposalJSON: ProposalJSON | null;
  proposalIdValue: string;
  session: Session;
}) => {
  dispatch(
    proposalsApi.util.upsertQueryData("getProposalById", { proposalId: proposalIdValue, session }, proposalJSON),
  );
};

const invalidateProposalById = (dispatch: AppDispatch, proposalIdValue: string) => {
  dispatch(proposalsApi.util.invalidateTags([{ type: "Proposal", id: proposalIdValue }]));
};

const updateProposalByIdCache = ({
  dispatch,
  proposalJSON,
  proposalIdValue,
  session,
}: {
  dispatch: AppDispatch;
  proposalJSON: ProposalJSON;
  proposalIdValue: string;
  session: Session;
}) => {
  upsertProposalById({ dispatch, proposalJSON, proposalIdValue, session });
  dispatch(addProposalJSON(proposalJSON));
};

const handleUnauthorizedProposal = ({
  dispatch,
  proposalIdValue,
  session,
}: {
  dispatch: AppDispatch;
  proposalIdValue: string;
  session: Session;
}) => {
  upsertProposalById({ dispatch, proposalJSON: null, proposalIdValue, session });
  dispatch(removeProposal(new Guid(proposalIdValue)));
};

const callbacks = withCallbacks<RootState, AppDispatch>()
  .add("proposal-created", (payload: unknown) => (dispatch, getState) => {
    const incomingProposalJSON = ProposalSchema.validateJSON(payload);
    if (!incomingProposalJSON.id) {
      throw new Error("Received proposal-created event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});
    const state = getState();
    const existingProposal = state.proposals.byId[incomingProposalJSON.id];
    if (existingProposal) {
      const incomingLastUpdated = AHDate.fromJSON(incomingProposalJSON.lastUpdated);
      const existingLastUpdated = AHDate.fromJSON(existingProposal.lastUpdated);
      if (existingLastUpdated && incomingLastUpdated && existingLastUpdated.isAfter(incomingLastUpdated)) {
        return; // ignore stale create
      }
    }
    updateProposalByIdCache({
      dispatch,
      proposalIdValue: incomingProposalJSON.id,
      proposalJSON: incomingProposalJSON,
      session,
    });
    invalidateProposalIndexes(dispatch);
    recordLatestRevisionUpdateFromProposal({ dispatch, incomingProposalJSON });
  })
  .add("proposal-updated", (payload: unknown) => (dispatch, getState) => {
    const incomingProposalIdValue = (payload as { id: string } | null | undefined)?.id;
    if (!incomingProposalIdValue) {
      throw new Error("Received proposal-updated event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});

    invalidateProposalIndexes(dispatch);

    const state = getState();
    const existingProposal = state.proposals.byId[incomingProposalIdValue];
    const incomingProposalLastUpdated = AHDate.fromJSON((payload as { lastUpdated?: string }).lastUpdated);
    const existingLastUpdated = AHDate.fromJSON(existingProposal?.lastUpdated) ?? undefined;
    if (incomingProposalLastUpdated && existingLastUpdated?.isAfter(incomingProposalLastUpdated)) {
      return; // ignore stale update
    }
    const isUnauthorized = (payload as { isUnauthorized: boolean } | null | undefined)?.isUnauthorized ?? false;
    if (isUnauthorized) {
      handleUnauthorizedProposal({ dispatch, proposalIdValue: incomingProposalIdValue, session });
      return;
    }

    let incomingProposalJSON;
    try {
      incomingProposalJSON = ProposalSchema.validateJSON(payload);
    } catch {
      const partialProposalUpdate = getPartialProposalUpdate(payload);
      if (existingProposal && partialProposalUpdate) {
        updateProposalByIdCache({
          dispatch,
          proposalIdValue: incomingProposalIdValue,
          proposalJSON: {
            ...existingProposal,
            ...partialProposalUpdate,
          },
          session,
        });
        return;
      }

      invalidateProposalById(dispatch, incomingProposalIdValue);
      return;
    }

    updateProposalByIdCache({
      dispatch,
      proposalIdValue: incomingProposalIdValue,
      proposalJSON: incomingProposalJSON,
      session,
    });
    recordLatestRevisionUpdateFromProposal({ dispatch, incomingProposalJSON });

    const incomingProposal = Proposal.fromJSON({
      json: incomingProposalJSON,
      currentUser: session.user!,
      options: { withRedline: true },
    });
    if (!incomingProposal?.id || incomingProposal?.isSubmitted) return;

    const incomingBuilder = incomingProposalJSON.userBuilderJSON
      ? (ProposalBuilder.fromJSON({
          json: incomingProposalJSON.userBuilderJSON,
          session,
        }) ?? undefined)
      : undefined;

    const existingOriginalBuilderJSON = state.proposals.builders.original[incomingProposal.id.value];
    const existingOriginalBuilder = ProposalBuilder.fromJSON({
      json: existingOriginalBuilderJSON,
      session,
    });

    const existingUpdatedBuilderJSON = state.proposals.builders.updated[incomingProposal.id.value];
    const existingUpdatedBuilder = ProposalBuilder.fromJSON({
      json: existingUpdatedBuilderJSON,
      session,
    });

    let originalBuilder = existingOriginalBuilder;
    let updatedBuilder = existingUpdatedBuilder;
    updatedBuilder ??= incomingBuilder;
    if (!updatedBuilder) {
      return;
    }
    if (!originalBuilder) {
      console.warn("Original proposal builder missing, setting to updated builder", {
        proposalId: incomingProposal.id.value,
      });
      originalBuilder = updatedBuilder;
    }

    if (
      existingUpdatedBuilder?.lastUpdated &&
      incomingBuilder?.lastUpdated.isAfter(existingUpdatedBuilder.lastUpdated)
    ) {
      originalBuilder = incomingBuilder;
      updatedBuilder = incomingBuilder;
    }

    if (incomingProposal.lastUpdated?.isAfter(updatedBuilder.lastUpdated)) {
      originalBuilder = new ProposalBuilder({
        session,
        startingSpec: incomingProposal.spec,
      });
      updatedBuilder = updatedBuilder.mergeUpdatedSpec(incomingProposal.spec);
    }
    dispatch(
      replaceOriginalProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: originalBuilder,
      }),
    );
    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: updatedBuilder,
      }),
    );
  })
  .add("proposal-deleted", (payload: unknown) => (dispatch) => {
    const incomingProposalIdValue = (payload as ProposalDeletedEvent)?.id;
    if (!incomingProposalIdValue) {
      throw new Error("Received proposal-updated event with no proposal ID");
    }
    const session = Session.loadFromStorage(() => {});
    dispatch(
      proposalsApi.util.invalidateTags([
        { type: "Proposal", id: "LIST" },
        { type: "Proposal", id: "DraftCount" },
        { type: "Proposal", id: "ActiveCount" },
        { type: "Proposal", id: "ArchiveCount" },
      ]),
    );
    dispatch(
      proposalsApi.util.upsertQueryData("getProposalById", { proposalId: incomingProposalIdValue, session }, null),
    );

    dispatch(
      replaceUpdatedProposalBuilder({
        proposalId: new Guid(incomingProposalIdValue),
        builder: undefined,
      }),
    );
    dispatch(removeProposal(new Guid(incomingProposalIdValue)));
  });

export const proposalsSignalRReduxMiddleware = reduxSignalRMiddleware<RootState, AppDispatch>({
  callbacks,
  connection: proposalHubConnection,
  connectionHealth: { key: "proposals", label: "Proposals" },
  shouldConnectionStartImmediately: true,
});
